import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';
import { todayInUAEString } from '../../../utils/timezone';

const userOption = (user) => ({
  value: user._id,
  label: [user.name || 'Unnamed user', user.email || user.phone].filter(Boolean).join(' — '),
});

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [plans, setPlans] = useState([]);
  // Picked users kept as react-select options so their labels survive between searches.
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    max_discount_amount: '',
    min_order_amount: '',
    user_scope: 'all',
    plan_ids: [],
    usage_type: 'single',
    per_user_limit: '',
    total_usage_limit: '',
    start_date: '',
    expiry_date: '',
    status: true,
  });

  useEffect(() => {
    getPlans();
    if (id) getSingleData();
  }, [id]);

  const getPlans = async () => {
    try {
      const response = await ApiService.request({ method: 'GET', url: 'admin/membership-plans-list' });
      if (response.data.status) setPlans(response.data.data || []);
    } catch (error) {
      // Handled by ApiService.
    }
  };

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/promo-codes/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const promo = data.data;
        setFormData({
          code: promo.code || '',
          title: promo.title || '',
          description: promo.description || '',
          discount_type: promo.discount_type || 'percentage',
          discount_value: promo.discount_value ?? '',
          max_discount_amount: promo.max_discount_amount ?? '',
          min_order_amount: promo.min_order_amount ?? '',
          user_scope: promo.user_scope || 'all',
          // Populated plans come back as objects; older responses as ids.
          plan_ids: (promo.plan_ids || []).map((plan) => String(plan?._id ?? plan)),
          usage_type: promo.usage_type || 'single',
          per_user_limit: promo.usage_type === 'multiple' ? (promo.per_user_limit ?? '') : '',
          total_usage_limit: promo.total_usage_limit ?? '',
          start_date: promo.start_date ? todayInUAEString(new Date(promo.start_date)) : '',
          expiry_date: promo.expiry_date ? todayInUAEString(new Date(promo.expiry_date)) : '',
          status: !!promo.status,
        });
        setSelectedUsers((promo.user_ids || []).map(userOption));
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const loadUsers = async (search) => {
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: 'admin/promo-codes/users',
        data: { search },
      });
      return response.data.status ? (response.data.data || []).map(userOption) : [];
    } catch (error) {
      return [];
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const code = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setFormData((prev) => ({ ...prev, code }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      toast.error('Code is required');
      return;
    }
    if (!(Number(formData.discount_value) > 0)) {
      toast.error('Discount value must be greater than 0');
      return;
    }
    if (formData.discount_type === 'percentage' && Number(formData.discount_value) > 100) {
      toast.error('Percentage discount cannot exceed 100');
      return;
    }
    if (formData.user_scope === 'specific' && selectedUsers.length === 0) {
      toast.error('Select at least one user');
      return;
    }
    if (formData.start_date && formData.expiry_date && formData.expiry_date < formData.start_date) {
      toast.error('Expiry date must be on or after the start date');
      return;
    }

    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'POST',
        url: `admin/promo-codes/createOrUpdate/${id || ''}`,
        data: {
          ...formData,
          code: formData.code.trim().toUpperCase(),
          user_ids: formData.user_scope === 'specific' ? selectedUsers.map((u) => u.value) : [],
        },
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/admin/promo-codes');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsLoader(false);
    }
  };

  const planOptions = plans.map((plan) => ({ value: String(plan._id), label: `${plan.title} (AED ${plan.price ?? 0})` }));

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Promo Code' : 'Add Promo Code'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/promo-codes" className="text-muted text-hover-primary">Promo Codes</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body py-6">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Code</label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        className="form-control form-control-solid"
                        placeholder="e.g. WELCOME20"
                        maxLength={30}
                      />
                      <button type="button" className="btn btn-light-primary" onClick={generateCode}>
                        Generate
                      </button>
                    </div>
                    <div className="form-text">Letters, numbers, dashes or underscores. Members enter it in the app.</div>
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. Welcome offer"
                    />
                  </div>

                  <div className="col-md-12 mb-6">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      rows="2"
                      placeholder="Internal note about this offer"
                    />
                  </div>

                  {/* Discount */}
                  <div className="col-md-12 mb-3">
                    <h5 className="text-dark">Discount</h5>
                  </div>

                  <div className="col-md-3 mb-6">
                    <label className="required form-label">Discount Type</label>
                    <select
                      name="discount_type"
                      value={formData.discount_type}
                      onChange={handleChange}
                      className="form-select form-control-solid"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed amount (AED)</option>
                    </select>
                  </div>

                  <div className="col-md-3 mb-6">
                    <label className="required form-label">
                      {formData.discount_type === 'percentage' ? 'Discount (%)' : 'Discount (AED)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={formData.discount_type === 'percentage' ? 100 : undefined}
                      step="0.01"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder={formData.discount_type === 'percentage' ? 'e.g. 20' : 'e.g. 100'}
                    />
                  </div>

                  {formData.discount_type === 'percentage' && (
                    <div className="col-md-3 mb-6">
                      <label className="form-label">Max Discount (AED)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="max_discount_amount"
                        value={formData.max_discount_amount}
                        onChange={handleChange}
                        className="form-control form-control-solid"
                        placeholder="No cap"
                      />
                    </div>
                  )}

                  <div className="col-md-3 mb-6">
                    <label className="form-label">Min Order Amount (AED)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="min_order_amount"
                      value={formData.min_order_amount}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="Any amount"
                    />
                  </div>

                  <div className="col-md-12 mb-6">
                    <label className="form-label">Applicable Plans</label>
                    <Select
                      isMulti
                      options={planOptions}
                      value={planOptions.filter((o) => formData.plan_ids.includes(o.value))}
                      onChange={(opts) => setFormData((prev) => ({ ...prev, plan_ids: (opts || []).map((o) => o.value) }))}
                      placeholder="All plans"
                      classNamePrefix="react-select"
                    />
                    <div className="form-text">Leave empty to allow the code on every membership plan.</div>
                  </div>

                  {/* Who can use it */}
                  <div className="col-md-12 mb-3">
                    <h5 className="text-dark">Eligibility &amp; Usage</h5>
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Who can use it</label>
                    <select
                      name="user_scope"
                      value={formData.user_scope}
                      onChange={handleChange}
                      className="form-select form-control-solid"
                    >
                      <option value="all">Anyone</option>
                      <option value="specific">Specific users</option>
                    </select>
                    <div className="form-text">
                      {formData.user_scope === 'specific'
                        ? 'Listed under "View available promos" in the app for these users.'
                        : 'Not listed in the app — members type the code in.'}
                    </div>
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Usage per user</label>
                    <select
                      name="usage_type"
                      value={formData.usage_type}
                      onChange={handleChange}
                      className="form-select form-control-solid"
                    >
                      <option value="single">Single use (once per user)</option>
                      <option value="multiple">Multiple uses per user</option>
                    </select>
                  </div>

                  {formData.user_scope === 'specific' && (
                    <div className="col-md-12 mb-6">
                      <label className="required form-label">Users</label>
                      <AsyncSelect
                        isMulti
                        cacheOptions
                        defaultOptions
                        loadOptions={loadUsers}
                        value={selectedUsers}
                        onChange={(opts) => setSelectedUsers(opts || [])}
                        placeholder="Search by name, email or phone…"
                        classNamePrefix="react-select"
                      />
                    </div>
                  )}

                  {formData.usage_type === 'multiple' && (
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Max uses per user</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        name="per_user_limit"
                        value={formData.per_user_limit}
                        onChange={handleChange}
                        className="form-control form-control-solid"
                        placeholder="Unlimited"
                      />
                    </div>
                  )}

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Total uses (all users)</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      name="total_usage_limit"
                      value={formData.total_usage_limit}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="Unlimited"
                    />
                  </div>

                  {/* Validity */}
                  <div className="col-md-12 mb-3">
                    <h5 className="text-dark">Validity</h5>
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                    />
                    <div className="form-text">Leave empty to start immediately.</div>
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Expiry Date</label>
                    <input
                      type="date"
                      name="expiry_date"
                      value={formData.expiry_date}
                      min={formData.start_date || undefined}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                    />
                    <div className="form-text">Valid through the end of this day (UAE time). Leave empty for no expiry.</div>
                  </div>

                  <div className="col-md-6 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                        id="status"
                      />
                      <label className="form-check-label" htmlFor="status">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  <Link to="/admin/promo-codes" className="btn btn-light">Cancel</Link>
                  <button type="submit" className="btn btn-base" disabled={isLoader}>
                    {isLoader ? <Loader text="Saving" size="20" /> : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
